import type { CSSProperties } from "react";

// ─── Landscape generic templates (classic / elegant / modern) ────────────────
/**
 * Wide landscape certificate (15:8 ≈ 1.875:1).
 * Reads clearly on screen and prints well on landscape paper.
 */
export const CERTIFICATE_CSS_W = 1200;
export const CERTIFICATE_CSS_H = 640;
const DPR = 2;

// ─── Mehul portrait templates (mehul-1 / mehul-2) ────────────────────────────
/**
 * Portrait certificate sized to A4 at 96 dpi.
 * Template JPEG files live in /public/.
 */
export const MEHUL_CSS_W = 794;
export const MEHUL_CSS_H = 1123;
const MEHUL_DPR = 2;

const CERTIFICATE_LOGO_SRC = "/logo-mehul-jlec.png";
const MEHUL_TEMPLATE_1_SRC = "/certificate_template_1.jpeg";
const MEHUL_TEMPLATE_2_SRC = "/certificate_template_2.jpeg";

// ─── Image caches ─────────────────────────────────────────────────────────────
let cachedCertificateLogo: HTMLImageElement | null = null;
let certificateLogoPromise: Promise<HTMLImageElement | null> | null = null;

let cachedMehulT1: HTMLImageElement | null = null;
let cachedMehulT2: HTMLImageElement | null = null;
let mehulT1Promise: Promise<HTMLImageElement | null> | null = null;
let mehulT2Promise: Promise<HTMLImageElement | null> | null = null;

/**
 * Eagerly loads the Mehul logo used by the generic landscape templates.
 * Cached after the first successful load; resolves to null on failure.
 */
export function loadCertificateLogo(): Promise<HTMLImageElement | null> {
  if (cachedCertificateLogo) return Promise.resolve(cachedCertificateLogo);
  if (certificateLogoPromise) return certificateLogoPromise;
  if (typeof window === "undefined") return Promise.resolve(null);

  certificateLogoPromise = new Promise((resolve) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      cachedCertificateLogo = img;
      resolve(img);
    };
    img.onerror = () => {
      certificateLogoPromise = null;
      resolve(null);
    };
    img.src = CERTIFICATE_LOGO_SRC;
  });
  return certificateLogoPromise;
}

function getCachedCertificateLogo(): HTMLImageElement | null {
  if (cachedCertificateLogo?.complete && cachedCertificateLogo.naturalWidth > 0) {
    return cachedCertificateLogo;
  }
  return null;
}

function loadMehulTemplateImg(which: 1 | 2): Promise<HTMLImageElement | null> {
  if (typeof window === "undefined") return Promise.resolve(null);

  if (which === 1) {
    if (cachedMehulT1) return Promise.resolve(cachedMehulT1);
    if (mehulT1Promise) return mehulT1Promise;
    mehulT1Promise = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => { cachedMehulT1 = img; resolve(img); };
      img.onerror = () => { mehulT1Promise = null; resolve(null); };
      img.src = MEHUL_TEMPLATE_1_SRC;
    });
    return mehulT1Promise;
  } else {
    if (cachedMehulT2) return Promise.resolve(cachedMehulT2);
    if (mehulT2Promise) return mehulT2Promise;
    mehulT2Promise = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => { cachedMehulT2 = img; resolve(img); };
      img.onerror = () => { mehulT2Promise = null; resolve(null); };
      img.src = MEHUL_TEMPLATE_2_SRC;
    });
    return mehulT2Promise;
  }
}

/**
 * Pre-loads both Mehul portrait template JPEG images.
 * Call this early (e.g. on component mount) so the first render is instant.
 */
export function loadMehulTemplates(): Promise<void> {
  return Promise.all([loadMehulTemplateImg(1), loadMehulTemplateImg(2)]).then(() => undefined);
}

function getCachedMehulTemplate(which: 1 | 2): HTMLImageElement | null {
  const img = which === 1 ? cachedMehulT1 : cachedMehulT2;
  return img?.complete && img.naturalWidth > 0 ? img : null;
}

// ─── Visual model ─────────────────────────────────────────────────────────────
export type CertificateVisualModel = {
  template: "classic" | "elegant" | "modern" | "mehul-1" | "mehul-2";
  institutionName: string;
  subtitle: string;
  recipientName: string;
  courseTitle: string;
  grade: string | null;
  certificateNumber: string;
  issuedDateLabel: string;
  signatoryTitle: string | null;
  /** Mehul-template–specific (optional, ignored by landscape templates) */
  studentId?: string;
  dateOfBirth?: string;
  gender?: string;
  totalLessonHours?: string;
  attendance?: string;
  textBook?: string;
};

/** Returns true when the template uses the portrait Mehul JPEG background. */
export function isMehulTemplate(t: CertificateVisualModel["template"]): boolean {
  return t === "mehul-1" || t === "mehul-2";
}

// ─── Canvas setup ─────────────────────────────────────────────────────────────
/**
 * Configures the canvas dimensions and returns the 2-D context.
 * Pass `portrait = true` when rendering a Mehul template.
 */
export function setupCertificateCanvas(
  canvas: HTMLCanvasElement,
  portrait = false,
): { ctx: CanvasRenderingContext2D; w: number; h: number } | null {
  const cssW = portrait ? MEHUL_CSS_W : CERTIFICATE_CSS_W;
  const cssH = portrait ? MEHUL_CSS_H : CERTIFICATE_CSS_H;
  const dpr = portrait ? MEHUL_DPR : DPR;
  const w = cssW * dpr;
  const h = cssH * dpr;
  canvas.width = w;
  canvas.height = h;
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  canvas.style.display = "block";
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  return { ctx, w, h };
}

/**
 * Returns the CSS `aspect-ratio` that keeps the canvas from being squashed.
 * Pass `portrait = true` for Mehul templates.
 */
export function certificateAspectRatioStyle(portrait = false): CSSProperties {
  if (portrait) return { aspectRatio: `${MEHUL_CSS_W} / ${MEHUL_CSS_H}` };
  return { aspectRatio: `${CERTIFICATE_CSS_W} / ${CERTIFICATE_CSS_H}` };
}

// ─── Landscape renderer helpers ───────────────────────────────────────────────
type Palette = {
  bg0: string; bg1: string; borderOuter: string; borderInner: string;
  accent: string; textMuted: string; textMain: string; seal: string;
};

function paletteForTemplate(t: "classic" | "elegant" | "modern"): Palette {
  switch (t) {
    case "elegant":
      return { bg0: "#fffdf7", bg1: "#faf6e8", borderOuter: "#b8860b", borderInner: "#d4af37",
               accent: "#5c3d1e", textMuted: "#7a6a52", textMain: "#2c1810", seal: "#8b6914" };
    case "modern":
      return { bg0: "#f8fafc", bg1: "#f1f5f9", borderOuter: "#334155", borderInner: "#64748b",
               accent: "#0ea5e9", textMuted: "#64748b", textMain: "#0f172a", seal: "#0f172a" };
    default:
      return { bg0: "#f8fbff", bg1: "#eef4fc", borderOuter: "#1e3a5f", borderInner: "#3b5b8c",
               accent: "#1e3a5f", textMuted: "#5c6f8c", textMain: "#0f172a", seal: "#1e3a5f" };
  }
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function roundedRect(
  ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function drawCornerFlourish(
  ctx: CanvasRenderingContext2D, x: number, y: number,
  size: number, quad: 0 | 1 | 2 | 3, color: string,
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate((quad * Math.PI) / 2);
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, 0); ctx.lineTo(size, 0);
  ctx.moveTo(0, 0); ctx.lineTo(0, size);
  ctx.arc(size, size, size * 0.45, Math.PI, Math.PI * 1.5);
  ctx.stroke();
  ctx.restore();
}

// ─── Landscape renderer ───────────────────────────────────────────────────────
function drawLandscapeCertificate(
  ctx: CanvasRenderingContext2D,
  w: number, h: number,
  m: CertificateVisualModel,
): void {
  const t = m.template as "classic" | "elegant" | "modern";
  const p = paletteForTemplate(t);
  const pad = w * 0.06;
  const innerW = w - pad * 2;

  const g = ctx.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, p.bg0); g.addColorStop(1, p.bg1);
  ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);

  ctx.strokeStyle = p.borderOuter; ctx.lineWidth = DPR * 3;
  roundedRect(ctx, pad * 0.35, pad * 0.35, w - pad * 0.7, h - pad * 0.7, 16 * DPR); ctx.stroke();
  ctx.strokeStyle = p.borderInner; ctx.lineWidth = DPR * 1.2;
  roundedRect(ctx, pad * 0.75, pad * 0.75, w - pad * 1.5, h - pad * 1.5, 12 * DPR); ctx.stroke();

  const corner = innerW * 0.08;
  drawCornerFlourish(ctx, pad + 8 * DPR, pad + 8 * DPR, corner, 0, p.borderInner);
  drawCornerFlourish(ctx, w - pad - 8 * DPR, pad + 8 * DPR, corner, 1, p.borderInner);
  drawCornerFlourish(ctx, w - pad - 8 * DPR, h - pad - 8 * DPR, corner, 2, p.borderInner);
  drawCornerFlourish(ctx, pad + 8 * DPR, h - pad - 8 * DPR, corner, 3, p.borderInner);

  const cx = w / 2;
  const cy = pad + 50 * DPR;
  const logoRadius = 30 * DPR;
  const logo = getCachedCertificateLogo();
  ctx.textAlign = "center"; ctx.textBaseline = "middle";

  if (logo) {
    ctx.save();
    ctx.beginPath(); ctx.arc(cx, cy, logoRadius, 0, Math.PI * 2); ctx.closePath();
    ctx.fillStyle = "#ffffff"; ctx.fill(); ctx.clip();
    const size = logoRadius * 2;
    const ratio = logo.naturalWidth / logo.naturalHeight || 1;
    const drawW = ratio >= 1 ? size * ratio : size;
    const drawH = ratio >= 1 ? size : size / ratio;
    ctx.drawImage(logo, cx - drawW / 2, cy - drawH / 2, drawW, drawH);
    ctx.restore();
    ctx.beginPath(); ctx.arc(cx, cy, logoRadius, 0, Math.PI * 2);
    ctx.strokeStyle = p.seal; ctx.lineWidth = DPR * 1.5; ctx.stroke();
  } else {
    ctx.beginPath(); ctx.arc(cx, cy, 22 * DPR, 0, Math.PI * 2);
    ctx.fillStyle = `${p.seal}18`; ctx.fill();
    ctx.strokeStyle = p.seal; ctx.lineWidth = DPR * 2; ctx.stroke();
    ctx.font = `italic ${11 * DPR}px Georgia, "Times New Roman", serif`;
    ctx.fillStyle = p.seal; ctx.fillText("✶", cx, cy);
  }

  let y = cy + 44 * DPR;
  ctx.fillStyle = p.textMuted;
  ctx.font = `${7 * DPR}px system-ui, sans-serif`;
  ctx.letterSpacing = `${1.8 * DPR}px`;
  ctx.fillText(m.institutionName.toUpperCase(), cx, y);
  ctx.letterSpacing = "0";

  y += 22 * DPR;
  ctx.strokeStyle = p.accent; ctx.lineWidth = DPR;
  ctx.beginPath(); ctx.moveTo(cx - innerW * 0.22, y); ctx.lineTo(cx + innerW * 0.22, y); ctx.stroke();

  y += 28 * DPR;
  ctx.font = `bold ${9 * DPR}px Georgia, "Times New Roman", serif`;
  ctx.fillStyle = p.accent; ctx.fillText(m.subtitle.toUpperCase(), cx, y);

  y += 36 * DPR;
  ctx.font = `${8 * DPR}px Georgia, serif`; ctx.fillStyle = p.textMuted;
  ctx.fillText("This is to certify that", cx, y);

  y += 36 * DPR;
  const nameFontSize = Math.max(16 * DPR, Math.min(28 * DPR, innerW / Math.max(m.recipientName.length * 0.52, 14)));
  ctx.font = `italic bold ${nameFontSize}px Georgia, "Times New Roman", serif`;
  ctx.fillStyle = p.textMain;
  const nameLines = wrapLines(ctx, m.recipientName, innerW * 0.82);
  for (const line of nameLines) { ctx.fillText(line, cx, y); y += 34 * DPR; }

  y += 8 * DPR;
  ctx.font = `${8 * DPR}px Georgia, serif`; ctx.fillStyle = p.textMuted;
  ctx.fillText("has successfully completed the program", cx, y);

  y += 32 * DPR;
  ctx.font = `600 ${11 * DPR}px Georgia, serif`; ctx.fillStyle = p.textMain;
  const courseLines = wrapLines(ctx, m.courseTitle, innerW * 0.85);
  for (const line of courseLines) { ctx.fillText(line, cx, y); y += 28 * DPR; }

  if (m.grade?.trim()) {
    y += 6 * DPR;
    ctx.font = `${8 * DPR}px Georgia, serif`; ctx.fillStyle = p.textMuted;
    ctx.fillText(`with distinction of grade ${m.grade.trim()}`, cx, y);
    y += 26 * DPR;
  } else { y += 12 * DPR; }

  ctx.font = `${7 * DPR}px system-ui, sans-serif`; ctx.fillStyle = p.textMuted;
  ctx.fillText(`Certificate ID  ${m.certificateNumber}   ·   Issued  ${m.issuedDateLabel}`, cx, y);

  if (m.signatoryTitle?.trim()) {
    const sx = w - pad - 80 * DPR;
    const sy = h - pad - 52 * DPR;
    ctx.strokeStyle = p.textMuted; ctx.lineWidth = DPR * 0.8;
    ctx.beginPath(); ctx.moveTo(sx - 100 * DPR, sy); ctx.lineTo(sx + 20 * DPR, sy); ctx.stroke();
    ctx.font = `${7 * DPR}px Georgia, serif`; ctx.fillStyle = p.textMuted; ctx.textAlign = "center";
    ctx.fillText(m.signatoryTitle.trim(), sx - 40 * DPR, sy + 18 * DPR);
  }
  ctx.textAlign = "left";
}

// ─── Mehul portrait renderer ──────────────────────────────────────────────────
/**
 * Draws a Mehul certificate by:
 *   1. Painting the JPEG template as a full-canvas background.
 *   2. Overlaying the student-specific text at calibrated positions.
 *
 * All CSS-pixel coordinates are defined at 794 × 1123 and scaled by DPR.
 */
function drawMehulCertificate(
  ctx: CanvasRenderingContext2D,
  w: number, h: number,
  m: CertificateVisualModel,
): void {
  const dpr = w / MEHUL_CSS_W; // MEHUL_DPR = 2 when canvas is set up correctly
  const tNum: 1 | 2 = m.template === "mehul-2" ? 2 : 1;
  const tImg = getCachedMehulTemplate(tNum);

  // ── 1. Background ───────────────────────────────────────────────────────────
  if (tImg) {
    ctx.drawImage(tImg, 0, 0, w, h);
    // Temporary pixel scanner to find the logo position
    try {
      const imgData = ctx.getImageData(0, 0, w, Math.floor(350 * dpr));
      const data = imgData.data;
      let minX = w, maxX = 0, minY = h, maxY = 0;
      let count = 0;
      for (let y = Math.floor(100 * dpr); y < Math.floor(300 * dpr); y++) {
        for (let x = Math.floor(250 * dpr); x < Math.floor(550 * dpr); x++) {
          const idx = (y * w + x) * 4;
          const r = data[idx];
          const g = data[idx + 1];
          const b = data[idx + 2];
          // Look for strong red pixels (the outer red circle of the logo)
          if (r > 160 && g < 60 && b < 60) {
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
            count++;
          }
        }
      }
      const cx = (minX + maxX) / 2 / dpr;
      const cy = (minY + maxY) / 2 / dpr;
      const radius = (maxX - minX) / 2 / dpr;
      console.log("LOGO SCAN:", { minX, maxX, minY, maxY, count, cx, cy, radius });
      ctx.save();
      ctx.fillStyle = "blue";
      ctx.font = `${16 * dpr}px sans-serif`;
      ctx.fillText(`LOGO SCAN: cx=${cx.toFixed(1)}, cy=${cy.toFixed(1)}, r=${radius.toFixed(1)}`, 30 * dpr, 40 * dpr);
      ctx.restore();
    } catch (e) {
      console.error("Scanner failed:", e);
    }
  } else {
    // Fallback until the JPEG loads
    ctx.fillStyle = tNum === 2 ? "#ede8d5" : "#f5f0e6";
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = "#8b1a1a"; ctx.lineWidth = 4 * dpr;
    ctx.strokeRect(20 * dpr, 20 * dpr, w - 40 * dpr, h - 40 * dpr);
    ctx.fillStyle = "#999"; ctx.font = `${10 * dpr}px sans-serif`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText("Loading template…", w / 2, h / 2);
    ctx.textAlign = "left"; ctx.textBaseline = "alphabetic";
  }

  // ── 2. Text helper ───────────────────────────────────────────────────────────
  /**
   * Draw `text` at CSS-pixel position (cssX, cssY).
   * All sizes and positions are in CSS pixels; this function scales to canvas pixels.
   */
  const put = (
    text: string,
    cssX: number, cssY: number, sizePt: number,
    opts: { align?: CanvasTextAlign; bold?: boolean; color?: string } = {},
  ) => {
    if (!text) return;
    const { align = "left", bold = false, color = "#111111" } = opts;
    ctx.save();
    ctx.font = `${bold ? "bold " : ""}${sizePt * dpr}px 'Hiragino Sans', 'Yu Gothic', 'Meiryo', Arial, sans-serif`;
    ctx.fillStyle = color;
    ctx.textAlign = align;
    ctx.textBaseline = "alphabetic";
    ctx.fillText(text, cssX * dpr, cssY * dpr);
    ctx.restore();
  };

  // ── 3. Form field values ─────────────────────────────────────────────────────
  // These are drawn directly over the blank underline areas in the template.

  // Row 1: 氏名 (Name)  +  学生 (Student Id)
  if (m.recipientName?.trim()) put(m.recipientName.trim(), 231, 466, 11.5);
  if (m.studentId?.trim())     put(m.studentId.trim(), 557, 466, 11.5);

  // Row 2: 生年月日 (Date of Birth)  +  性別 (Gender)
  if (m.dateOfBirth?.trim()) put(m.dateOfBirth.trim(), 283, 513, 11.5);
  if (m.gender?.trim())      put(m.gender.trim(), 540, 513, 11.5);

  // ── 4. Curriculum paragraph ──────────────────────────────────────────────────
  // Sample the exact JPEG background colour from a clean area (below paragraph,
  // right gutter) so the cover rectangle blends seamlessly with the template.
  let paraBg = tNum === 2 ? "#ece6d3" : "#f2ede1";
  if (tImg) {
    try {
      const sx = Math.max(0, Math.floor(690 * dpr));
      const sy = Math.max(0, Math.floor(858 * dpr));
      const px = ctx.getImageData(sx, sy, 6, 6);
      let sr = 0, sg = 0, sb = 0;
      for (let i = 0; i < px.data.length; i += 4) {
        sr += px.data[i]; sg += px.data[i + 1]; sb += px.data[i + 2];
      }
      const n = px.data.length / 4;
      paraBg = `rgb(${Math.round(sr / n)},${Math.round(sg / n)},${Math.round(sb / n)})`;
    } catch { /* use hardcoded fallback */ }
  }
  ctx.fillStyle = paraBg;
  ctx.fillRect(63 * dpr, 638 * dpr, 669 * dpr, 200 * dpr);

  // Inline mixed-weight text renderer —
  // draws segments with alternating bold/regular weight in a single horizontal run.
  // This matches the original certificate style where English labels are bold.
  type Seg = { text: string; bold: boolean };
  const mixedLine = (segs: Seg[], cssX: number, cssY: number, sizePt: number) => {
    let cx = cssX * dpr;
    const y = cssY * dpr;
    const baseFont = `'Hiragino Sans', 'Yu Gothic', 'Meiryo', Arial, sans-serif`;
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#111111";
    for (const seg of segs) {
      ctx.font = `${seg.bold ? "bold " : ""}${sizePt * dpr}px ${baseFont}`;
      ctx.fillText(seg.text, cx, y);
      cx += ctx.measureText(seg.text).width;
    }
  };

  const paraX  = 68;
  let paraY    = 664;
  const lineH  = 24.5; // CSS px between baselines — slightly more breathing room
  const pSize  = 11.5; // point size — matches original certificate body text

  const hours      = m.totalLessonHours?.trim() || "___";
  const attendance = m.attendance?.trim()       || "___";
  const grade      = m.grade?.trim()            || "___";
  const textBook   = m.textBook?.trim()         || "___";

  // Line 1 — Japanese certification statement
  mixedLine([{ text: "上記の者は、本学校で日本語を勉強したことを証明する。", bold: false }], paraX, paraY, pSize);
  paraY += lineH;

  // Line 2 — Lesson hours  ·  Attendance
  mixedLine([
    { text: "総授業時間 ",                  bold: false },
    { text: "(Total Lesson Hour)",          bold: true  },
    { text: ` ${hours} 時間,  出席 `,       bold: false },
    { text: "(Attendance)",                 bold: true  },
    { text: ` ${attendance}%,`,             bold: false },
  ], paraX, paraY, pSize);
  paraY += lineH;

  // Line 3 — Grade  ·  Text Book
  mixedLine([
    { text: "評価グレード ",                bold: false },
    { text: "(Obtained Grade)",             bold: true  },
    { text: ` ${grade},  教科書 `,          bold: false },
    { text: "(Text Book)",                  bold: true  },
    { text: ` ${textBook}。`,               bold: false },
  ], paraX, paraY, pSize);

  // ── 5. Certificate number / ID (small, below paragraph) ─────────────────────
  if (m.certificateNumber && !m.certificateNumber.startsWith("PREVIEW")) {
    put(`証明書番号: ${m.certificateNumber}`, 68, 864, 8.5, { color: "#555" });
  }

  // ── 6. Issue Date ─────────────────────────────────────────────────────────────
  // Template shows: "Issue Date: ___年___月___日"
  // Try to parse issuedDateLabel back to a Date for year/month/day extraction.
  let issueYear = "", issueMonth = "", issueDay = "";
  const parsed = new Date(m.issuedDateLabel);
  if (Number.isFinite(parsed.getTime())) {
    issueYear  = parsed.getFullYear().toString();
    issueMonth = (parsed.getMonth() + 1).toString();
    issueDay   = parsed.getDate().toString();
  } else {
    // Fallback: print the full label starting at the year position
    issueYear = m.issuedDateLabel;
  }

  if (issueYear)  put(issueYear,  153, 1019, 10.5);
  if (issueMonth) put(issueMonth, 200, 1019, 10.5);
  if (issueDay)   put(issueDay,   243, 1019, 10.5);
}

// ─── Public draw entry point ──────────────────────────────────────────────────
/**
 * Draws a certificate onto an already-configured canvas.
 * The canvas MUST have been set up with `setupCertificateCanvas(canvas, portrait)`
 * where `portrait = isMehulTemplate(model.template)`.
 */
export function drawCertificate(
  ctx: CanvasRenderingContext2D,
  w: number, h: number,
  m: CertificateVisualModel,
): void {
  if (isMehulTemplate(m.template)) {
    drawMehulCertificate(ctx, w, h, m);
    return;
  }
  drawLandscapeCertificate(ctx, w, h, m);
}

// ─── PNG export & print helpers ───────────────────────────────────────────────
export async function certificateToPngDataUrl(m: CertificateVisualModel): Promise<string> {
  const portrait = isMehulTemplate(m.template);
  if (portrait) {
    await loadMehulTemplates();
  } else {
    await loadCertificateLogo();
  }
  const c = document.createElement("canvas");
  const setup = setupCertificateCanvas(c, portrait);
  if (!setup) return "";
  drawCertificate(setup.ctx, setup.w, setup.h, m);
  return c.toDataURL("image/png");
}

export async function downloadCertificatePng(m: CertificateVisualModel, filenameBase: string): Promise<void> {
  const dataUrl = await certificateToPngDataUrl(m);
  if (!dataUrl) return;
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = `${filenameBase.replace(/[^a-z0-9-_]+/gi, "-")}.png`;
  a.click();
}

/**
 * Opens a print window with the rendered certificate.
 * The window is opened synchronously to avoid pop-up blockers.
 *
 * @returns false if the pop-up was blocked.
 */
export async function openCertificatePrintWindow(m: CertificateVisualModel): Promise<boolean> {
  const target = window.open("", "_blank");
  if (!target) return false;
  try {
    target.document.write(
      `<!DOCTYPE html><html><head><title>Certificate</title></head><body style="font-family:system-ui,sans-serif;padding:24px;">Preparing certificate…</body></html>`,
    );
    target.document.close();
  } catch { /* ignore */ }

  const dataUrl = await certificateToPngDataUrl(m);
  if (!dataUrl) {
    try { target.document.body.textContent = "Could not render certificate."; } catch { /* ignore */ }
    return false;
  }

  const portrait = isMehulTemplate(m.template);
  const pageSize = portrait ? "portrait" : "landscape";

  try {
    target.document.open();
    target.document.write(`<!DOCTYPE html><html><head><title>Certificate</title>
  <style>
    @page { size: ${pageSize}; margin: 8mm; }
    body { margin:0; display:flex; justify-content:center; align-items:center; min-height:100vh; background:#fff; }
    img { max-width:100%; height:auto; }
  </style></head><body>
  <img src="${dataUrl}" alt="Certificate" id="c" />
  <script>
    const i=document.getElementById('c');
    i.onload=function(){ window.print(); };
  </script>
  </body></html>`);
    target.document.close();
  } catch { return false; }
  return true;
}

export function certificatePreviewCssSize(portrait = false): { width: number; height: number } {
  if (portrait) return { width: MEHUL_CSS_W, height: MEHUL_CSS_H };
  return { width: CERTIFICATE_CSS_W, height: CERTIFICATE_CSS_H };
}
